import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DATE_FORMAT_DISPLAY, DEFAULT_FORMAT, DEFAULT_TABLE_ITEM_SIZE, VOUCHER_STATE_ACTIVE, VOUCHER_STATE_DONE, VOUCHER_STATE_PENDING, VOUCHER_STATE_SCHEDULED, VOUCHER_TYPE_PERCENT } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { voucherStateOptions } from '@constants/masterData';
import useFetch from '@hooks/useFetch';
import useListModal from '@hooks/useListModal';
import useRestaurant from '@hooks/useRestaurant';
import useTranslate from '@hooks/useTranslate';
import { BaseTable, BaseTooltip } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime, formatMoney } from '@utils';
import { Button, Modal, Tag } from 'antd';
import dayjs from 'dayjs';

import VoucherForm from './VoucherForm';

const VoucherListPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { restaurantId } = useParams();
    const stateValue = translate.formatKeys(voucherStateOptions, ['label']);
    const { execute: executeDone } = useFetch(apiConfig.voucher.done);

    const { currentSettingParse } = useRestaurant();

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        detail,
        setDetail,
        titleModal,
        executeSave,
        loadingSave,
        form,
        openModal,
        onShowModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
    } = useListModal({
        apiConfig: apiConfig.voucher,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE,
            objectName: translate.formatMessage(commonMessage.voucher),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            const prepareGetListParams = funcs.prepareGetListParams;
            funcs.prepareGetListParams = (params) => {
                const oldParams = prepareGetListParams(params);
                if (Number(oldParams.state) === VOUCHER_STATE_SCHEDULED) {
                    return {
                        ...oldParams,
                        state: VOUCHER_STATE_ACTIVE,
                        isSchedule: true,
                    };
                } else {
                    return {
                        ...oldParams,
                        isSchedule: false,
                    };
                }
            };
            funcs.additionalActionColumnButtons = () => ({
                edit: (record) => {
                    const hasPermission = mixinFuncs.hasPermission([apiConfig.voucher.update.baseURL]);

                    const isDisabled = !hasPermission || !(record.state === VOUCHER_STATE_PENDING);

                    return (
                        <BaseTooltip type="edit" objectName={translate.formatMessage(commonMessage.voucher)}>
                            <Button
                                type="link"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDetail(record);
                                    onShowModal();
                                }}
                                style={{ padding: 0 }}
                                disabled={isDisabled}
                            >
                                <EditOutlined style={{ color: isDisabled ? 'gray' : 'blue' }} />
                            </Button>
                        </BaseTooltip>
                    );
                },
                done: (record) => {
                    const isDisabled = record.state === VOUCHER_STATE_DONE;
                    return (
                        <BaseTooltip type="done" objectName={translate.formatMessage(commonMessage.voucher)}>
                            <Button
                                type="link"
                                disabled={isDisabled}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    Modal.confirm({
                                        title: translate.formatMessage(commonMessage.confirm),
                                        content: translate.formatMessage(commonMessage.doneVoucherConfirm),
                                        centered: true,
                                        okText: translate.formatMessage(commonMessage.yes),
                                        cancelText: translate.formatMessage(commonMessage.no),
                                        onOk: () => {
                                            executeDone({
                                                pathParams: { id: record.id },
                                                onCompleted: () => {
                                                    mixinFuncs.getList(true);
                                                },
                                            });
                                        },
                                    });
                                }}
                                style={{ padding: 0 }}
                            >
                                <CloseOutlined style={{ color: isDisabled ? 'gray' : 'red' }} />
                            </Button>
                        </BaseTooltip>
                    );
                },
            });
        },
    });

    const columns = [
        // {
        //     title: '#',
        //     dataIndex: 'index',
        //     key: 'id',
        //     render: (text, record, index) => {
        //         return orderNumber(pagination, index);
        //     },
        //     width: 50,
        // },
        {
            title: translate.formatMessage(commonMessage.name),
            dataIndex: 'name',
            render: (name, record) => (
                <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/${restaurantId}/voucher/redemption?voucherId=${record.id}`);
                    }}
                >
                    {name}
                </Button>
            ),
        },
        {
            title: translate.formatMessage(commonMessage.voucherCode),
            dataIndex: 'code',
            width: 160,
            // render: (code, record) => (
            //     <Button
            //         type="link"
            //         style={{ padding: 0 }}
            //         onClick={(e) => {
            //             e.stopPropagation();
            //             navigate(`/${restaurantId}/voucher/redemption?voucherId=${record.id}`);
            //         }}
            //     >
            //         {code}
            //     </Button>
            // ),
        },
        {
            title: translate.formatMessage(commonMessage.voucherValue),
            dataIndex: 'value',
            width: 200,
            align: 'center',
            render: (value, record) => {
                if (record.type === VOUCHER_TYPE_PERCENT) {
                    return `${value}%`;
                }
                return formatMoney(value, currentSettingParse);
            },
        },
        {
            title: translate.formatMessage(commonMessage.minOrderValue),
            dataIndex: 'minOrderValue',
            width: 200,
            align: 'center',
            render: (minOrderValue) => {
                return minOrderValue && formatMoney(minOrderValue, currentSettingParse);
            },
        },
        {
            title: translate.formatMessage(commonMessage.startDate),
            dataIndex: 'startDate',
            width: 180,
            render: (startDate) => {
                return convertUtcToLocalTime(startDate, DEFAULT_FORMAT, DATE_FORMAT_DISPLAY);
            },
        },
        {
            title: translate.formatMessage(commonMessage.endDate),
            dataIndex: 'endDate',
            width: 180,
            render: (endDate) => {
                return convertUtcToLocalTime(endDate, DEFAULT_FORMAT, DATE_FORMAT_DISPLAY);
            },
        },
        {
            title: translate.formatMessage(commonMessage.state),
            dataIndex: 'state',
            width: 120,
            align: 'center',
            render: (state, record) => {
                let displayState = state;
                let color = stateValue?.find((item) => item.value === state)?.color;

                if (state === VOUCHER_STATE_ACTIVE) {
                    const now = dayjs();
                    const localStart = dayjs(convertUtcToLocalTime(record.startDate, DEFAULT_FORMAT, DEFAULT_FORMAT), DEFAULT_FORMAT);
                    const localEnd = dayjs(convertUtcToLocalTime(record.endDate, DEFAULT_FORMAT, DEFAULT_FORMAT), DEFAULT_FORMAT);

                    if (now.isBefore(localStart) || now.isAfter(localEnd)) {
                        displayState = 'scheduled';
                        color = 'orange';
                    }
                }

                const label = displayState === 'scheduled'
                    ? translate.formatMessage(commonMessage.scheduled)
                    : stateValue?.find((item) => item.value === state)?.label;

                return (
                    <Tag color={color}>
                        <div style={{ padding: '0 4px', fontSize: 14 }}>{label}</div>
                    </Tag>
                );
            },
        },
        mixinFuncs.renderActionColumn(
            {
                edit: true,
                done: mixinFuncs.hasPermission([apiConfig.voucher.done.baseURL]),
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'code',
            placeholder: translate.formatMessage(commonMessage.voucherCode),
        },
        {
            key: 'state',
            placeholder: translate.formatMessage(commonMessage.state),
            type: FieldTypes.SELECT,
            options: stateValue,
            submitOnChanged: true,
        },
    ];

    return (
        <PageWrapper routes={[{ breadcrumbName: translate.formatMessage(commonMessage.voucher) }]}>
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                actionBar={mixinFuncs.renderActionBar({
                    objectName: translate.formatMessage(commonMessage.voucher),
                })}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        pagination={pagination}
                        style={{ cursor: 'pointer' }}
                        loading={loading || loadingSave}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    title={
                        detail
                            ? translate.formatMessage(commonMessage.updateVoucher)
                            : translate.formatMessage(commonMessage.createVoucher)
                    }
                    width={800}
                    isModalVisible={openModal}
                    handleCancel={onCancelModal}
                    isEditing={!!detail}
                    form={form}
                    loading={loadingSave}
                >
                    <VoucherForm
                        detail={detail}
                        form={form}
                        isEditing={isEditing}
                        onSubmit={onSave}
                        actions={mixinFuncs.renderActions()}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default VoucherListPage;
